import { ChevronLeft } from '@mui/icons-material';
import {
  Alert,
  Box,
  Container,
  Skeleton,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Button,
  ButtonVariants,
  Carousel,
  LearningPathProgressImage,
  useCarouselScroll,
} from '@/components';
import { Modals, Routes } from '@/constants';
import {
  CreditsDurationInfo,
  EpisodeCard,
  EpisodeCardGutterSpacing,
  EpisodeCardVariants,
  EpisodeCount,
  FreeTrialPrompt,
} from '@/containers';
import { EVENT_CONSTANTS } from '@/services';
import {
  LearningPathsUtils,
  playerSessionsSelectors,
  selectSessionUserId,
  selectUserCompliance,
  uiActions,
  useAppDispatch,
  useFetchEpisodesQuery,
  useFetchLearningPathQuery,
} from '@/store';

import { LpBreadcrumbs } from '../LearningPaths/components';
import { CAROUSEL_ITEM_WIDTH, CAROUSEL_ITEMS_PER_SCROLL } from './constants';
import { EpisodeStatusButton } from './EpisodeStatusButton';
import { LearningPathSkeleton } from './LearningPathSkeleton';

const LearningPathTopNav = () => {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();
  const { state } = useLocation();

  const { from } = state || {};
  const backToRoute = from?.pathname?.startsWith(Routes.LEARNING_PATHS)
    ? `${from.pathname}${from.search ?? ''}`
    : Routes.LEARNING_PATHS;

  if (isLg) return <LpBreadcrumbs />;

  return (
    <Button
      sx={{
        mb: 1,
      }}
      label="Back to Learning Paths"
      variant={ButtonVariants.PLAIN}
      onClick={() => navigate(backToRoute)}
      startIcon={<ChevronLeft sx={{ width: 24, height: 24 }} />}
      fullWidth={false}
      colorOverride={theme.palette.text.light}
    />
  );
};

export const LearningPath = () => {
  const { learningPathId } = useParams();
  const dispatch = useAppDispatch();

  const userId = useSelector(selectSessionUserId);
  const compliance = useSelector(selectUserCompliance);
  const playerSessions = useSelector(playerSessionsSelectors.selectEntities);

  const parsedLearningPathId = Number(learningPathId);
  const { data: learningPath, isLoading: isLearningPathLoading } =
    useFetchLearningPathQuery(
      {
        compliance,
        learningPathId: parsedLearningPathId,
      },
      { skip: !parsedLearningPathId },
    );

  const episodeIds = learningPath?.episodes?.map(e => e.episodeId) || [];

  const {
    data: episodes,
    isLoading: isEpisodesLoading,
    isUninitialized: isEpisodesUninitialized,
  } = useFetchEpisodesQuery(
    { compliance, episodeIds },
    { skip: !learningPath },
  );

  const isLpEpisodeCardsLoading =
    isLearningPathLoading || isEpisodesLoading || isEpisodesUninitialized;

  const episodeCount = learningPath?.episodes?.length || 0;

  const referrer = {
    page: EVENT_CONSTANTS.PAGE.LEARNING_PATH_DETAILS,
    component: EVENT_CONSTANTS.COMPONENT.LEARNING_PATH_EPISODE,
  };

  useEffect(() => {
    if (!learningPath && !isLearningPathLoading && !userId) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.SIGN_UP,
          params: {
            referrer: { page: EVENT_CONSTANTS.PAGE.LEARNING_PATH_DETAILS },
          },
        }),
      );
    }
  }, [learningPath, isLearningPathLoading, userId, dispatch]);

  const { containerId, scroll, isNearBeginning, isNearEnd } = useCarouselScroll(
    {
      itemWidth: CAROUSEL_ITEMS_PER_SCROLL * CAROUSEL_ITEM_WIDTH,
      uKey: 'learning-path-episodes',
      referrer,
    },
  );

  const focusedEpisodeIndex =
    learningPath?.episodes.findIndex(
      episode =>
        playerSessions[episode.episodeId]?.status !== 'verified' &&
        playerSessions[episode.episodeId]?.status !== 'completed',
    ) ?? 0;

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container || focusedEpisodeIndex <= 0) return () => {};

    // Scroll to first incomplete episode if it exists
    const timer = setTimeout(() => {
      container.scrollTo({
        left: (focusedEpisodeIndex - 0.5) * CAROUSEL_ITEM_WIDTH,
        behavior: 'smooth',
      });
    }, 700);

    return () => clearTimeout(timer);
  }, [containerId, focusedEpisodeIndex]);

  if (isLearningPathLoading) {
    return (
      <Box>
        <LearningPathTopNav />
        <LearningPathSkeleton />
      </Box>
    );
  }

  if (!learningPath) {
    return userId ? (
      <Box>
        <LearningPathTopNav />
        <Alert severity="error">
          <Typography variant="body3">
            Sorry, the learning path you were looking for was not found.
          </Typography>
        </Alert>
      </Box>
    ) : (
      <Container>
        <Box mb={6}>
          <Skeleton variant="rounded" height={200} />
        </Box>
      </Container>
    );
  }

  const { durationPlayed } = LearningPathsUtils.calculateProgress({
    lps: [learningPath],
    playerSessions,
  })[0];

  return (
    <Box flexGrow={1}>
      <LearningPathTopNav />
      <Box sx={styles.root}>
        <Box>
          <Box sx={styles.imageContainer}>
            <LearningPathProgressImage
              src={learningPath.coverImage}
              durationTotal={learningPath.duration}
              durationPlayed={durationPlayed}
            />
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Box>
              <Typography variant="hero3" mb={2}>
                {learningPath.title}
              </Typography>
              <Box sx={styles.subheader}>
                <CreditsDurationInfo
                  credits={learningPath.credits}
                  duration={learningPath.duration}
                />
                <EpisodeCount episodeCount={episodeCount} />
              </Box>
            </Box>
          </Box>
          <Typography
            variant="body1"
            sx={{ '& p': { margin: 0 } }}
            dangerouslySetInnerHTML={{ __html: learningPath.description }}
          />
        </Box>
        <Box>
          <Typography sx={styles.sectionTitle}>
            {episodes?.length} Episodes
          </Typography>
          <Box sx={styles.carouselContainer}>
            <Carousel
              containerId={containerId}
              leftScrollIcon={!isNearBeginning}
              rightScrollIcon={!isNearEnd}
              scroll={scroll}>
              {episodes
                ? episodes.map((episode, idx) => (
                    <Box sx={styles.cardContainer} key={episode.episodeId}>
                      <EpisodeCard
                        episode={episode}
                        isLoading={isLpEpisodeCardsLoading}
                        referrer={referrer}
                      />
                      <Box>
                        <EpisodeStatusButton
                          episodeId={episode.episodeId}
                          showProgressLine={idx < episodeIds.length - 1}
                          referrer={referrer}
                        />
                      </Box>
                    </Box>
                  ))
                : episodeIds.map(episodeId => (
                    <Box sx={styles.cardContainer} key={episodeId}>
                      <EpisodeCard referrer={referrer} />
                    </Box>
                  ))}
            </Carousel>
          </Box>
        </Box>
        {learningPath.keyTakeaways && (
          <Box>
            <Typography variant="h2" gutterBottom>
              Key Takeaways
            </Typography>
            <Typography
              variant="body1"
              sx={styles.takeaways}
              dangerouslySetInnerHTML={{ __html: learningPath.keyTakeaways }}
            />
          </Box>
        )}
        {!userId && (
          <FreeTrialPrompt
            referrer={{ page: EVENT_CONSTANTS.PAGE.LEARNING_PATH_DETAILS }}
          />
        )}
      </Box>
    </Box>
  );
};

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: {
      xs: 3,
      lg: 6,
    },
  },
  imageContainer: {
    float: {
      xs: 'none',
      md: 'right',
    },
    display: 'flex',
    justifyContent: 'center',
    ml: {
      xs: 0,
      md: 6,
    },
    mb: {
      xs: 2,
      md: 0,
    },
  },
  subheader: {
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  carouselContainer: (theme: Theme) => ({
    backgroundColor: 'grey50',
    borderRadius: 4,
    overflow: 'hidden',
    scrollbarWidth: 'none',
    px: {
      xs: theme.dimensions.appLayout.px.xs,
      lg: 4.5,
    },
    pt: {
      xs: theme.dimensions.appLayout.px.xs,
      lg: 4.5,
    },
    pb: 3,
  }),
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    mr: EpisodeCardGutterSpacing[EpisodeCardVariants.MEDIUM],
    mb: 0.5,
  },
  takeaways: {
    '& ul': {
      margin: 0,
      display: 'flex',
      flexWrap: 'wrap',
      paddingInlineStart: 2,
    },
    '& li': {
      pl: 0,
      ml: 0,
      pr: {
        xs: 0,
        md: 8,
      },
      mb: 0,
      width: { xs: '100%', md: '50%' },
    },
  },
  sectionTitle: (theme: Theme) => ({
    ...theme.typography.h2,
    mb: 1,
  }),
};
